import React from 'react';

import PropTypes from 'prop-types';
import { FaCheck } from 'react-icons/fa';
import styled from 'styled-components';

import { getRbidByFacetNameAndFacetOption } from '~/gridv1_1/utils/filters';
import { sizeByKey } from '~/shared/utils/sizeConstants';
import useGender from '~/shared/utils/useGender';
import { FormattedMessage } from '~/techstyle-shared/react-intl';
import { useDomain } from '~/techstyle-shared/redux-core';

const DynamicFormattedMessage = FormattedMessage;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px; // Adjusted spacing between checkbox and label
`;

const CheckboxBox = styled.label`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  min-height: 40px;
  ${({ theme }) => theme.gridFilterStyles.gridLabelStyle};

  background-color: ${({ checked, theme }) => (checked ? 'black' : 'white')};
  border-radius: 0.25rem;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${({ checked, theme }) =>
    checked ? 'white' : theme.colors.palette.shadow90};
  text-align: center;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.palette.shadow50};
    outline-offset: 2px;
  }

  ${({ theme }) => theme.gridFilterStyles.gridInputStyle};
`;

const StyledCheckbox = styled.input.attrs({ type: 'checkbox' })`
  appearance: none;
  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 0; // Hide native checkbox
`;

const CheckIcon = styled(FaCheck)`
  pointer-events: none;
  position: absolute;
  color: white;
  font-size: 18px;
  opacity: 0;
  transition: opacity 0.2s ease;

  ${StyledCheckbox}:checked + ${CheckboxBox} & {
    opacity: 1; // Show the checkmark when checked
  }
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 4px 0px;
  ${({ theme }) => theme.gridFilterStyles.listingFilterStyle};
`;

const SuperScriptLabel = styled.sup`
  font-size: 10px;
  opacity: 0.75;
  margin-top: -4px;
`;

function MobileGridGroupedFilterButtonInput({
  id,
  name,
  label,
  superScriptLabel,
  superScriptCategory,
  checked,
  onChange,
  filterName,
}) {
  const domain = useDomain();
  const { gender } = useGender();
  const localizedLabel =
    sizeByKey({
      domain,
      gender,
      category: filterName,
      key: label,
    }) || label;

  const snakecaseFilterName = filterName.replace(/-/g, '_').toLowerCase();
  const slugifiedRBLabel = getRbidByFacetNameAndFacetOption(
    snakecaseFilterName,
    localizedLabel
  );

  // This is currently intended for SXF Custom Bra Filters only
  // Force female for Bra sizes, for the edge case where a user is male browsing
  const localizedSuperScriptLabel =
    superScriptLabel &&
    superScriptCategory &&
    sizeByKey({
      domain,
      gender: 'F',
      category: superScriptCategory,
      key: superScriptLabel,
    });

  const handleChange = (event) => {
    onChange(event.target.checked);
  };

  return (
    <CheckboxWrapper data-autotag={`filter-${filterName}-option`}>
      <CheckboxBox checked={checked}>
        <StyledCheckbox
          id={id}
          name={name}
          checked={checked}
          onChange={handleChange}
          aria-checked={checked}
          role="checkbox"
        />
        <CheckIcon aria-hidden="true" />
        <Label htmlFor={id}>
          {localizedSuperScriptLabel && (
            <SuperScriptLabel>{localizedSuperScriptLabel}</SuperScriptLabel>
          )}
          <DynamicFormattedMessage
            id={`site_product_filter.${slugifiedRBLabel}`}
            defaultMessage={localizedLabel}
          />
        </Label>
      </CheckboxBox>
    </CheckboxWrapper>
  );
}

MobileGridGroupedFilterButtonInput.propTypes = {
  checked: PropTypes.bool.isRequired,
  filterName: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  superScriptCategory: PropTypes.string,
  superScriptLabel: PropTypes.string,
};

export default MobileGridGroupedFilterButtonInput;
